import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { useCompaniesStore } from "./companiesStore";
import {
  HydratedCompany,
  CompanyPrimaryContact,
  CSV_IMPORT_TYPE,
  Permission,
  canEditOwnedEntity,
} from "@forgepoint/shared";
import { Loader, Trash2, Upload } from "lucide-react";
import CsvImportSheet from "@/modules/csvImports/CsvImportSheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { usePaginatedEntitySearch } from "@/lib/usePaginatedEntitySearch";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { toast } from "sonner";
import { useUserDisplayNames } from "@/store/teamsStore";
import CompanyCreateSheet from "./CompanyCreateSheet";
import CompanyDetailSheet from "./CompanyDetailSheet";
import {
  useAuthStore,
  useHasAnyPermission,
  useHasPermission,
} from "@/store/authStore";

export default function CompaniesController() {
  const {
    companies,
    listLoading,
    listError,
    companyLoadingStates,
    fetchCompanies,
    deleteCompany,
  } = useCompaniesStore();

  const {
    entitiesArray: companiesArray,
    handleSearch,
    setDisplayedEntityIds: setDisplayedCompanyIds,
    page,
    fetchPage,
    refetchAfterRemoval,
    pagination,
  } = usePaginatedEntitySearch({
    entities: companies,
    fetchEntities: fetchCompanies,
  });

  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isImportSheetOpen, setIsImportSheetOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] =
    useState<HydratedCompany | null>(null);

  const navigate = useNavigate();
  const { companyId: detailCompanyId } = useParams<{ companyId?: string }>();

  const canEditAllCompanies = useHasPermission(Permission.COMPANIES_EDIT_ALL);
  const canEditOwnCompanies = useHasPermission(Permission.COMPANIES_EDIT_OWN);
  const canCreateCompanies = useHasAnyPermission(
    Permission.COMPANIES_EDIT_ALL,
    Permission.COMPANIES_EDIT_OWN,
  );
  const currentUserId = useAuthStore((state) => state.user?.id);

  /**
   * Whether the current user may edit (and delete) a specific company.
   * @param company - The company to check
   * @returns True when the user holds edit.all, or edit.own and owns the company
   */
  const canEditCompany = (company: HydratedCompany): boolean =>
    canEditOwnedEntity(
      canEditAllCompanies,
      canEditOwnCompanies,
      !!company.salesRepUserId && company.salesRepUserId === currentUserId,
    );

  const userDisplayNames = useUserDisplayNames();

  /**
   * Checks whether a company is synced from an external system.
   * @param company - The company to check
   * @returns True if the company has external mappings
   */
  const isCompanySynced = (company: HydratedCompany | null): boolean =>
    Boolean(company && company.externalMappings.length > 0);

  /**
   * Returns a display name for a primary contact, falling back through
   * first/last/email.
   * @param contact - The contact to get a display name for
   * @returns A human-readable display name
   */
  const getContactDisplayName = (contact: CompanyPrimaryContact) => {
    if (contact.firstName && contact.lastName) {
      return `${contact.firstName} ${contact.lastName}`;
    }
    if (contact.firstName) {
      return contact.firstName;
    }
    if (contact.lastName) {
      return contact.lastName;
    }
    return contact.email || "Unknown";
  };

  const columns: DataTableColumn<HydratedCompany>[] = [
    {
      key: "name",
      header: "Company Name",
      type: "string",
    },
    {
      key: "primaryContact",
      header: "Primary Contact",
      type: "string",
      width: 200,
      className: "hidden lg:table-cell",
      format: (_value: unknown, company: HydratedCompany) =>
        company.primaryContact
          ? getContactDisplayName(company.primaryContact)
          : "-",
    },
    {
      key: "salesRepUserId",
      header: "Sales Rep",
      type: "string",
      width: 180,
      className: "hidden lg:table-cell",
      format: (_value: unknown, company: HydratedCompany) => {
        if (!company.salesRepUserId) {
          return "-";
        }
        return userDisplayNames.get(company.salesRepUserId) ?? "-";
      },
    },
    {
      key: "email",
      header: "Email",
      type: "string",
      width: 220,
      className: "hidden md:table-cell",
    },
    {
      key: "phone",
      header: "Phone",
      type: "string",
      width: 150,
      className: "hidden lg:table-cell",
    },
    {
      key: "address.city",
      header: "City",
      type: "string",
      width: 150,
      className: "hidden xl:table-cell",
      format: (value) => (value || "-") as string,
    },
    {
      key: "address.state",
      header: "State",
      type: "string",
      width: 100,
      className: "hidden xl:table-cell",
      format: (value) => (value || "-") as string,
    },
    {
      key: "createdAt",
      header: "Created",
      type: "date",
      width: 120,
      className: "hidden sm:table-cell",
    },
    {
      key: "syncStatus",
      header: "Sync",
      width: 90,
      type: "string",
      className: "hidden md:table-cell",
      format: (_value: unknown, company: HydratedCompany) =>
        isCompanySynced(company) ? (
          <Badge variant="secondary">Synced</Badge>
        ) : (
          "-"
        ),
    },
    {
      key: "actions",
      header: "",
      width: 50,
      format: (_value: unknown, company: HydratedCompany) => {
        if (!canEditCompany(company)) {
          return null;
        }

        const synced = isCompanySynced(company);
        const loadingState = companyLoadingStates.get(company.id);
        const isDeleting = loadingState?.isLoadingDelete;

        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(company);
            }}
            disabled={synced || isDeleting}
          >
            {isDeleting ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        );
      },
    },
  ];

  /**
   * Refreshes the current page after a CSV import completes.
   */
  const handleImportComplete = async () => {
    await fetchPage(page);
  };

  /**
   * Opens the create company sheet.
   */
  const handleCreate = () => {
    setIsCreateSheetOpen(true);
  };

  /**
   * Opens the delete confirmation dialog for a company, or shows a toast if synced.
   * @param company - The company to delete
   */
  const handleDelete = (company: HydratedCompany) => {
    if (isCompanySynced(company)) {
      toast.info(
        "Synced companies cannot be deleted. Manage this record in the source system.",
      );
      return;
    }
    setSelectedCompany(company);
    setIsDeleteDialogOpen(true);
  };

  /**
   * Handles the callback after a company is successfully created.
   * @param company - The newly created company
   */
  const handleCreateSaved = (company: HydratedCompany) => {
    setDisplayedCompanyIds((prev) => [company.id, ...prev]);
  };

  /**
   * Confirms and executes the deletion of the selected company, clamping the
   * page when the last row of the final page is removed.
   */
  const handleConfirmDelete = async () => {
    if (!selectedCompany) {
      return;
    }

    if (isCompanySynced(selectedCompany)) {
      toast.info(
        "Synced companies cannot be deleted. Manage this record in the source system.",
      );
      setIsDeleteDialogOpen(false);
      setSelectedCompany(null);
      return;
    }

    try {
      const success = await deleteCompany(selectedCompany.id);
      if (success) {
        toast.success("Company deleted successfully");
        await refetchAfterRemoval();
      } else {
        toast.error("Failed to delete company");
      }
    } catch (_error) {
      toast.error("Failed to delete company");
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedCompany(null);
    }
  };

  if (listError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-16 text-center">
        <p className="text-sm text-destructive">
          Failed to load companies: {listError}
        </p>
        <Button variant="outline" size="sm" onClick={() => fetchPage(page)}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <DataTable
        columns={columns}
        data={companiesArray}
        onClickRow={(company) => navigate(`/companies/${company.id}`)}
        onChangeSearch={handleSearch}
        searchPlaceholder="Search companies..."
        className="h-full"
        isLoading={listLoading && companiesArray.length === 0}
        pagination={pagination}
        onCreate={canCreateCompanies ? handleCreate : undefined}
        createButtonText="New"
        toolbarActions={
          canCreateCompanies ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsImportSheetOpen(true)}
            >
              <Upload className="h-4 w-4 mr-1.5" />
              Import
            </Button>
          ) : undefined
        }
      />

      <CsvImportSheet
        importType={CSV_IMPORT_TYPE.COMPANIES}
        open={isImportSheetOpen}
        onOpenChange={setIsImportSheetOpen}
        onImportComplete={handleImportComplete}
      />

      <CompanyCreateSheet
        open={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        onSaved={handleCreateSaved}
      />

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        entityType="Company"
        itemName={selectedCompany?.name}
      />

      {detailCompanyId && (
        <CompanyDetailSheet
          open={!!detailCompanyId}
          onOpenChange={(open) => {
            if (!open) {
              navigate("/companies");
            }
          }}
          companyId={detailCompanyId}
        />
      )}
    </div>
  );
}
